import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import BringzzText from "../BringzzText";
import BringzzIcon from "../BringzzIcon";
import classnames from "classnames";
import { useTranslation } from 'react-i18next';
import useRequest from 'hooks/useRequest';

const BringzzPromoCard = ({
  className,
  backgroundImage: initialBackgroundImage,
  title: initialTitle,
  subTitle: initialSubTitle,
  ...props
}) => {
  const { t } = useTranslation();
  const { sendRequest } = useRequest();
  const [postData, setPostData] = useState({
    title: initialTitle,
    subTitle: initialSubTitle,
    backgroundImage: initialBackgroundImage,
    integrationId: null
  });
  const [hasBookmark, setHasBookmark] = useState(false);

  const fetchBookmarkStatus = async (integrationId) => {
    if (!integrationId) return;
    try {
      const response = await sendRequest({
        method: 'POST',
        url: `/module/bookmark/hasBookmark`,
        data: { elementUUID: integrationId }
      });
      setHasBookmark(response.status === 'success' ? true: false);
    } catch {
      setHasBookmark(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest({
          method: "GET",
          url: "/module/post/getSponsoredPosts",
        });
        
        const firstPost = response.data?.posts?.[0];
        if (firstPost) {
          const newData = {
            title: firstPost.headline,
            subTitle: firstPost.description || firstPost.imageCaption,
            backgroundImage: firstPost.images?.[0]?.content || '',
            integrationId: firstPost.integrationId
          };
          setPostData(newData);
          fetchBookmarkStatus(newData.integrationId);
        }
      } catch {
        setPostData({
          title: initialTitle,
          subTitle: initialSubTitle,
          backgroundImage: initialBackgroundImage,
          integrationId: null
        });
      }
    };

    fetchData();
  }, [sendRequest]);

  useEffect(() => {
    if (postData.integrationId) {
      fetchBookmarkStatus(postData.integrationId);
    }
  }, [postData.integrationId]);

  const handleBookmarkClick = async () => {
    if (!postData.integrationId) return;

    try {
      if (hasBookmark) {
        await sendRequest({
          method: 'POST',
          url: `/module/bookmark/removeBookmark`,
          data: { elementUUID: postData.integrationId }
        });
      } else {
        await sendRequest({
          method: 'POST',
          url: `/module/bookmark/addBookmark`,
          data: {
            type: 0,
            elementUUID: postData.integrationId
          }
        });
      }
      fetchBookmarkStatus(postData.integrationId);
    } catch {
      setHasBookmark(prev => !prev);
    }
  };

  const handleCopyPostUrl = async () => {
    try {
      const postUrl = `${window.location.origin}/post/details/${postData.integrationId}`;
      await navigator.clipboard.writeText(postUrl);
      setTimeout(() => {
        window.location.href = `/post/details/${postData.integrationId}`;
      }, 1000);
    } catch (error) {
      console.error('Failed to copy post URL:', error);
    }
  };

  return (
    <div className="px-4 py-8">
      <div className={classnames("relative overflow-hidden rounded-xl", className)} style={{ maxHeight: "530px" }}>
        <div className="absolute w-full h-full bg-gradient-to-t from-black/25 to-transparent"></div>
        
        <img
          className="w-full h-auto object-cover"
          src={postData.backgroundImage}
          alt="promo background"
          style={{ maxHeight: "530px" }}
        />
        
        <div className="absolute font-inter top-0 left-0 mt-6 ml-6 bg-black/60 text-white/50 p-2 text-xs">
          {t("sponsored_label")}
        </div>

        <div className="absolute bottom-0 pl-6 pr-8 py-8 w-full box-border">
          <div className="flex flex-col space-y-1">
            <BringzzText tag="h4" className="font-bold text-white">
              {postData.title}
            </BringzzText>
            <BringzzText tag="h4" className="text-white font-normal">
              {postData.subTitle}
            </BringzzText>
          </div>
          
          <div className="flex justify-between text-white mt-6">
            <BringzzIcon 
              icon={hasBookmark ? "IconBookmarkSolid" : "IconBookmark"}
              folder="HeroIcons" 
              size="20" 
              className={`cursor-pointer ${hasBookmark ? 'text-gold' : 'text-white'}`}
              onClick={handleBookmarkClick}
            />
            <BringzzIcon 
              icon="IconUpload" 
              folder="LineIcons" 
              size="18" 
              className="cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleCopyPostUrl}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

BringzzPromoCard.propTypes = {
  className: PropTypes.string,
  backgroundImage: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  subTitle: PropTypes.node,
};

export default BringzzPromoCard;